import { useState } from 'react';
import { Plus, X as XIcon, Image as ImageIcon, FileText } from 'lucide-react';
import MediumPickerModal from './MediumPickerModal';

const API = import.meta.env.VITE_API_URL || '';

function streamUrl(dateipfad) {
  if (!dateipfad) return '';
  return `${API}/api/prozesse/medien/stream/${encodeURIComponent(dateipfad)}`;
}

function PlayBadge() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="w-8 h-8 rounded-full bg-white/85 flex items-center justify-center shadow">
        <div className="w-0 h-0 border-y-[5px] border-y-transparent border-l-[8px] border-l-[#1A1A2E] ml-0.5" />
      </div>
    </div>
  );
}

function Thumbnail({ medium, onOpen, onUnlink, editable, t }) {
  const tt = (k, fb) => (t ? (t(k) || fb) : fb);
  const isImage = medium.dateityp === 'image';
  const isVideo = medium.dateityp === 'video';

  return (
    <div className="group relative w-20 h-20 rounded-lg overflow-hidden border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] shrink-0">
      <button
        type="button"
        onClick={() => onOpen?.(medium)}
        className="block w-full h-full"
        title={medium.dateiname}
      >
        {isImage ? (
          <img src={streamUrl(medium.dateipfad)} alt={medium.dateiname} className="w-full h-full object-cover" />
        ) : isVideo ? (
          <div className="relative w-full h-full bg-black">
            <video src={streamUrl(medium.dateipfad)} preload="metadata" muted className="w-full h-full object-cover" />
            <PlayBadge />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileText className="w-6 h-6 text-[var(--text-tertiary,#6B7280)] opacity-50" strokeWidth={1.5} />
          </div>
        )}
      </button>
      {editable && (
        <button
          type="button"
          onClick={() => onUnlink?.(medium)}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          title={tt('prozesse.schritt.medium_entfernen', 'Verknüpfung entfernen')}
        >
          <XIcon className="w-3 h-3" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

export default function SchrittMediaSection({
  prozessId,
  schrittId,
  medien = [],
  editable = false,
  onChanged,
  onOpenLightbox,
  t,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const tt = (k, fb) => (t ? (t(k) || fb) : fb);

  const handleUnlink = async (medium) => {
    if (!schrittId || !medium?.id) return;
    if (!window.confirm(tt('prozesse.schritt.medium_entfernen_confirm', 'Medium-Verknüpfung wirklich entfernen? Das Medium selbst bleibt erhalten.'))) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/prozesse/${prozessId}/schritte/${schrittId}/medien/${medium.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const linkedIds = medien.map(m => m.id);

  // Wenn nicht editable und keine Medien → nichts rendern
  if (!editable && medien.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[var(--text-tertiary,#6B7280)]">
          {tt('prozesse.schritt.medien_label', 'Medien')}
          {medien.length > 0 && ` (${medien.length})`}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 items-start">
        {medien.map(m => (
          <Thumbnail
            key={m.id}
            medium={m}
            editable={editable}
            onOpen={() => onOpenLightbox?.(medien, medien.findIndex(x => x.id === m.id))}
            onUnlink={handleUnlink}
            t={t}
          />
        ))}
        {editable && schrittId && (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={busy}
            className="w-20 h-20 rounded-lg border border-dashed border-[var(--border-card,#E5E7EB)] hover:border-[#338edc] text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] transition-colors flex flex-col items-center justify-center gap-1 disabled:opacity-50"
            title={tt('prozesse.schritt.medium_hinzufuegen', 'Medium hinzufügen')}
          >
            <Plus className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px]">{tt('prozesse.schritt.medium_hinzufuegen', 'Hinzufügen')}</span>
          </button>
        )}
      </div>
      {editable && !schrittId && (
        <p className="text-xs text-[var(--text-tertiary,#6B7280)] italic">
          {tt('prozesse.schritt.medien_hint_save_first', 'Schritt zuerst speichern, danach Medien verknüpfen')}
        </p>
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}

      <MediumPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        prozessId={prozessId}
        schrittId={schrittId}
        alreadyLinkedIds={linkedIds}
        onLinked={() => { setPickerOpen(false); onChanged?.(); }}
        t={t}
      />
    </div>
  );
}
